/**
 * Manufacturing - Furniture (Phase 6), following the client's production flow:
 *  - drawing & commitment per sales order (independent of order approval): drawing attached → reviewed
 *    by production → completion date committed → QC / dispatch planning → delivery date committed, each
 *    date written back to the sales order;
 *  - planning: unplanned orders → production job (operations from a route, employee allocation without
 *    double booking, raw-material plan);
 *  - procurement: MRN generated from the material plan → purchase requisition for shortages → PO →
 *    inward gate pass → GRN → material issued to the job;
 *  - labour: operations released to employees → started → activities logged → completed;
 *  - production start / in progress / complete → finished goods received into stock.
 */
import { z } from 'zod';
import { always } from './schemas';

const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const req = (label: string, max: number) => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const optDate = z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null);
const uuid = (msg: string) => z.string({ error: msg }).uuid(msg);
const uuidOpt = z.union([z.literal(''), z.string().uuid()]).nullable().optional().transform((v) => v || null);
const qty = (label = 'Quantity') => z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).max(1e9)).refine((n) => n > 0, `${label} must be above 0`);
const minutes = z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().int('Whole minutes').min(0).max(100_000));
const dateTime = z.string({ error: 'Enter date and time' }).regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/, 'Enter date and time');

// ---------- Setup ----------

const code20 = z.string().trim().min(1, 'Code is required').max(20).regex(/^[A-Z0-9_-]+$/, 'Use uppercase letters, numbers, dash');

export const OP_LOCATIONS = ['FACTORY', 'SITE'] as const;
export type OpLocation = (typeof OP_LOCATIONS)[number];
export const OP_LOCATION_LABELS: Record<OpLocation, string> = { FACTORY: 'In the factory', SITE: "At the customer's site" };
export const HANDLED_BY_LABELS = { PRODUCTION: 'Production (planned in the studio)', DELIVERY: 'Delivery & Dispatch module', INSTALLATION: 'Installation module (crews)' } as const;
export const MINUTES_ENTRIES = ['PER_PIECE', 'TOTAL'] as const;
export type MinutesEntryMode = (typeof MINUTES_ENTRIES)[number];
export const MINUTES_ENTRY_LABELS: Record<MinutesEntryMode, string> = { PER_PIECE: 'Per piece (× quantity)', TOTAL: 'Total for the item' };
/** Colour tones an operation may show (named palette tones; the web maps each to its classes). */
export const OP_COLOURS = ['amber', 'orange', 'yellow', 'lime', 'green', 'emerald', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink', 'rose', 'slate', 'stone'] as const;
/** Icons an operation may show (lucide names; the web maps each to its icon). */
export const OP_ICONS = ['scissors', 'ruler', 'puzzle', 'boxes', 'hammer', 'paint-roller', 'grid', 'layers', 'spool', 'shirt', 'sofa', 'badge-check', 'package', 'truck', 'wrench', 'drill', 'brush', 'sparkles', 'cog', 'circle'] as const;

/** Production Setup › Operations. */
export const operationTypeSchema = z.object({
  code: code20,
  name: req('Name', 80),
  /** Production section that performs it (Carpentry, Upholstery, Paint …). */
  sectionId: uuidOpt,
  location: z.enum(OP_LOCATIONS),
  skillRequired: z.boolean(),
  minutesEntry: z.enum(MINUTES_ENTRIES),
  canOverlapPrevious: z.boolean(),
  countsCapacity: z.boolean(),
  reworkAllowed: z.boolean(),
  /** PRODUCTION (planned here), DELIVERY (Delivery & Dispatch), INSTALLATION (installation crews). */
  handledBy: z.enum(['PRODUCTION', 'DELIVERY', 'INSTALLATION']).default('PRODUCTION'),
  needsMaterial: z.boolean().default(true),
  colour: z.union([z.literal(''), z.enum(OP_COLOURS)]),
  icon: z.union([z.literal(''), z.enum(OP_ICONS)]),
  description: z.string().trim().max(300).default(''),
  /** Old-system names of this operation (for reports on old jobs). */
  legacyNames: z.array(z.string().max(80)).max(30).default([]),
  isActive: z.boolean(),
});
export type OperationTypeInput = z.infer<typeof operationTypeSchema>;

export interface SetupOperation extends OperationTypeInput {
  id: string;
  version: number;
  sortOrder: number;
  section: string | null;
  mergedInto: string | null;
  /** Where it is used: categories, job operations, skills. Used operations can only be deactivated. */
  used: { categories: number; jobOperations: number; skills: number };
}

/** Production Setup › Categories: main and detailed operations, in the admin's order. */
export const categorySchema = z
  .object({
    code: code20,
    name: req('Name', 80),
    description: z.string().trim().max(300).default(''),
    isActive: z.boolean(),
    plannerMayReorder: z.boolean(),
    /** null = follow the planning rule. */
    allDetailedRequired: z.boolean().nullable(),
    skipMainAllowed: z.boolean(),
    /** null = the planning rule "Default plan type". */
    defaultPlanMode: z.enum(['SIMPLE', 'DETAILED']).nullable().default(null),
    operations: z
      .array(z.object({ operationTypeId: uuid('Choose the operation'), isMain: z.boolean(), required: z.boolean() }))
      .min(1, 'Add at least one operation')
      .max(40),
  })
  .superRefine((v, ctx) => {
    if (!v.operations.some((o) => o.isMain)) ctx.addIssue({ code: 'custom', path: ['operations'], message: 'Mark at least one main operation' });
    const seen = new Set<string>();
    v.operations.forEach((o, i) => {
      if (seen.has(o.operationTypeId)) ctx.addIssue({ code: 'custom', path: ['operations', i, 'operationTypeId'], message: 'This operation is already in the list' });
      seen.add(o.operationTypeId);
    });
  });
export type CategoryInput = z.infer<typeof categorySchema>;

export interface SetupCategory extends Omit<CategoryInput, 'operations'> {
  id: string;
  version: number;
  sortOrder: number;
  operations: { operationTypeId: string; code: string; name: string; isMain: boolean; required: boolean; isActive: boolean }[];
  jobs: number;
}

export const reorderSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(500) });

export const mergeOperationsSchema = z
  .object({ fromId: uuid('Choose the operation to merge'), intoId: uuid('Choose the operation it becomes'), reason: z.string().trim().min(5, 'Say why (at least 5 characters)').max(300) })
  .refine((v) => v.fromId !== v.intoId, { path: ['intoId'], message: 'Choose a different operation' });
export type MergeOperationsInput = z.infer<typeof mergeOperationsSchema>;

export const labourRateSchema = z
  .object({
    sectionId: uuidOpt,
    employeeId: uuidOpt,
    ratePerHour: z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: 'Enter the rate' }).min(0).max(10000)),
    /** Overhead per hour of this section (pre- and post-costing); empty = Accounting's furniture overhead rate. */
    overheadPerHour: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(10000).nullable()).default(null),
    effectiveFrom: z.string({ error: 'Enter the date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter the date'),
    notes: z.string().trim().max(200).default(''),
  })
  .refine((v) => !!v.sectionId !== !!v.employeeId, { path: ['employeeId'], message: 'Choose a section or an employee (one of them)' });
export type LabourRateInput = z.infer<typeof labourRateSchema>;
export interface LabourRateRow extends LabourRateInput {
  id: string;
  version: number;
  section: string | null;
  employee: string | null;
}

export const PLANNING_RULE_KEYS = [
  'presales.inqReviewMode',
  'presales.typedRateBy',
  'mfg.plan.jobModes',
  'mfg.plan.difficultyLabels',
  'mfg.plan.unusualMinutesPct',
  'mfg.plan.allDetailedRequired',
  'mfg.plan.allowOverlapDefault',
  'mfg.plan.sundayOvertime',
  'mfg.plan.shiftTimesOnly',
  'mfg.plan.materialApproval',
  'mfg.plan.replanMode',
  'mfg.plan.defaultPlanMode',
  'mfg.plan.dispatchDays',
  'mfg.plan.installDays',
  'presales.inqTimeLimits',
  'mfg.plan.timeLimits',
  'mfg.plan.materialLeadDays',
] as const;
export const JOB_MODE_LABELS = { ITEM: 'Per item', SUBCATEGORY: 'Per sub-category', ORDER: 'Whole order' } as const;

/** Excel import of the setup (rows read in the browser, checked and applied on the server). */
export const setupImportSchema = z.object({
  dryRun: z.boolean(),
  operations: z.array(z.record(z.string(), z.string())).max(500).default([]),
  categories: z.array(z.record(z.string(), z.string())).max(5000).default([]),
});
export type SetupImportInput = z.infer<typeof setupImportSchema>;
export interface SetupImportResult {
  applied: boolean;
  errors: { sheet: 'Operations' | 'Categories'; row: number; message: string }[];
  changes: { sheet: 'Operations' | 'Categories'; code: string; action: 'add' | 'change' | 'same'; detail: string }[];
}

export interface SetupHistoryRow {
  id: string;
  at: string;
  by: string | null;
  action: string;
  summary: string;
  changes: { field: string; from: unknown; to: unknown }[];
}

// ---------- Drawing & commitment ----------

export const DRAWING_STATUSES = ['NONE', 'ATTACHED', 'APPROVED', 'REJECTED'] as const;
export type DrawingStatus = (typeof DRAWING_STATUSES)[number];
export const DRAWING_STATUS_LABELS: Record<DrawingStatus, string> = { NONE: 'No drawing', ATTACHED: 'Awaiting review', APPROVED: 'Reviewed – OK', REJECTED: 'Sent back' };

export const COMMIT_STEPS = ['drawing_attached', 'drawing_review', 'completion_date', 'qc_planning', 'delivery_date'] as const;
export type CommitStep = (typeof COMMIT_STEPS)[number];

export const drawingAttachedSchema = z.object({ notes: opt(500) });
export const drawingReviewSchema = z.object({ approved: z.boolean(), notes: opt(500) }).refine((v) => v.approved || v.notes.length > 0, { path: ['notes'], message: 'Say what must change', ...always });
export const completionDateSchema = z.object({ completionDate: isoDate, notes: opt(500) });
export const qcPlanningSchema = z.object({ qcDate: isoDate, dispatchNotes: opt(500) });
export const deliveryDateSchema = z.object({ deliveryDate: isoDate, notes: opt(500) });

export interface CommitmentItem {
  orderId: string;
  orderNumber: string | null;
  orderStatus: import('./documents').DocStatus;
  orderDate: string;
  customer: string | null;
  requestedDate: string | null;
  drawingStatus: DrawingStatus;
  drawingFiles: number;
  drawingNotes: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  completionDate: string | null;
  qcDate: string | null;
  dispatchNotes: string;
  deliveryDate: string | null;
  productionStatus: import('./sales').ProductionStatus;
  nextStep: CommitStep | null;
  history: { step: string; at: string; by: string | null; notes: string }[];
}

// ---------- Production job ----------

export const JOB_STAGES = ['PLANNING', 'RELEASED', 'IN_PROGRESS', 'QC_PENDING', 'COMPLETED', 'CANCELLED'] as const;
export type JobStage = (typeof JOB_STAGES)[number];
export const JOB_STAGE_LABELS: Record<JobStage, string> = { PLANNING: 'Planning', RELEASED: 'Released to floor', IN_PROGRESS: 'In production', QC_PENDING: 'Quality check', COMPLETED: 'Complete – FG ready', CANCELLED: 'Cancelled' };
export const OP_STATUSES = ['PLANNED', 'RELEASED', 'IN_PROGRESS', 'DONE'] as const;
export type OpStatus = (typeof OP_STATUSES)[number];
export const OP_STATUS_LABELS: Record<OpStatus, string> = { PLANNED: 'Planned', RELEASED: 'Released', IN_PROGRESS: 'In progress', DONE: 'Done' };

export const jobSchema = z
  .object({
    docDate: isoDate,
    orderId: uuid('Choose the sales order'),
    routeId: uuidOpt,
    plannedStart: isoDate,
    plannedEnd: isoDate,
    notes: opt(1000),
    items: z.array(z.object({ soLineId: uuid('Pick the order line'), qty: qty() })).min(1, 'Add at least one item to make').max(100),
    operations: z
      .array(
        z.object({
          id: z.string().uuid().optional(),
          operationTypeId: uuid('Choose the operation'),
          plannedMinutes: minutes,
          plannedStart: optDate,
          plannedEnd: optDate,
          instructions: opt(500),
          allocations: z.array(z.object({ employeeId: uuid('Choose the employee'), startAt: dateTime, endAt: dateTime })).max(30).default([]),
        }),
      )
      .min(1, 'Add at least one operation')
      .max(40),
    materials: z.array(z.object({ itemId: uuid('Choose the material'), qty: qty(), opIndex: z.number().int().min(0).nullable().optional().transform((v) => v ?? null), notes: opt(300) })).max(300).default([]),
  })
  .refine((v) => v.plannedEnd >= v.plannedStart, { path: ['plannedEnd'], message: 'Cannot end before it starts', ...always })
  .refine((v) => v.operations.every((o) => o.allocations.every((a) => a.endAt > a.startAt)), { path: ['operations'], message: 'Each allocation must end after it starts', ...always });
export type JobInput = z.infer<typeof jobSchema>;

export const activitySchema = z
  .object({
    operationId: uuid('Pick the operation'),
    employeeId: uuid('Choose the employee'),
    startedAt: dateTime,
    endedAt: dateTime,
    notes: opt(300),
  })
  .refine((v) => v.endedAt > v.startedAt, { path: ['endedAt'], message: 'Must be after the start', ...always });
export type ActivityInput = z.infer<typeof activitySchema>;

export interface JobOperationItem {
  id: string;
  seq: number;
  operationType: { id: string; code: string; name: string };
  department: string | null;
  plannedMinutes: number;
  plannedStart: string | null;
  plannedEnd: string | null;
  instructions: string;
  status: OpStatus;
  isRework: boolean;
  startedAt: string | null;
  completedAt: string | null;
  actualMinutes: number;
  allocations: { id: string; employeeId: string; employee: string; startAt: string; endAt: string; minutes: number }[];
  activities: { id: string; employee: string; startedAt: string; endedAt: string; minutes: number; notes: string }[];
}

export interface JobDetail {
  id: string;
  /** Planned in the Planning Studio (then changed only there). */
  planningStudio: boolean;
  number: string | null;
  status: import('./documents').DocStatus;
  stage: JobStage;
  docDate: string;
  order: { id: string; number: string | null; customer: string | null; committedDate: string | null; completionDate: string | null };
  routeId: string | null;
  /** A repair job for an after-sales ticket. */
  serviceTicket: { id: string; number: string | null } | null;
  plannedStart: string;
  plannedEnd: string;
  notes: string;
  items: { id: string; soLineId: string; item: { id: string; code: string; name: string; uom: string } | null; description: string; qty: number; received: number }[];
  operations: JobOperationItem[];
  materials: { id: string; item: { id: string; code: string; name: string; uom: string }; qty: number; opIndex: number | null; requested: number; issued: number; issuedValue: number; onHand: number; notes: string; additional: boolean }[];
  mrns: { id: string; number: string | null; status: import('./documents').DocStatus }[];
  fgReceipts: { id: string; number: string | null; docDate: string; total: number }[];
  qcChecks: { id: string; round: number; result: QcResult; notes: string; checkedBy: string; checkedAt: string }[];
  costs: { material: number; labourMinutes: number; labour: number; overhead: number; total: number };
  costSheet: import('./costing').CostSheet;
  version: number;
  migrated: boolean;
  actions: import('./documents').WorkflowAction[];
}

export interface JobListItem {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  stage: JobStage;
  orderNumber: string | null;
  customer: string | null;
  plannedStart: string;
  plannedEnd: string;
  progress: number;
  late: boolean;
  migrated: boolean;
}

export interface UnplannedLine {
  orderId: string;
  orderNumber: string | null;
  orderDate: string;
  customer: string | null;
  committedDate: string | null;
  drawingStatus: DrawingStatus;
  lineId: string;
  item: { id: string; code: string; name: string; uom: string } | null;
  description: string;
  qty: number;
  planned: number;
  open: number;
}

// ---------- MRN ----------

export interface MrnSubstitute {
  id: string;
  item: { id: string; code: string; name: string; uom: string };
  qty: number;
  coversQty: number;
  issued: number;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  auto: boolean;
  costRequested: number;
  costSubstitute: number;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decisionNote: string;
}

export const substituteSchema = z.object({
  itemId: uuid('Pick the substitute item'),
  qty: qty('Quantity of the substitute'),
  coversQty: qty('How much of the requested item it replaces'),
  reason: z.string().trim().min(3, 'Give the reason').max(300),
});
export type SubstituteInput = z.infer<typeof substituteSchema>;

export interface PendingSubstitute extends MrnSubstitute {
  mrn: { id: string; number: string | null };
  job: { id: string; number: string | null };
  requested: { code: string; name: string; uom: string };
}

export interface MrnDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  job: { id: string; number: string | null };
  warehouseId: string | null;
  lines: {
    id: string;
    item: { id: string; code: string; name: string; uom: string };
    qty: number;
    onHand: number;
    short: number;
    /** Issued, in the requested item's units (substitutes count by what they replace). */
    issued: number;
    requisitioned: number;
    closedQty: number;
    open: number;
    substitutes: MrnSubstitute[];
  }[];
  closed: { at: string; reason: string; by: string } | null;
  requisitions: { id: string; number: string | null; status: import('./documents').DocStatus }[];
  issues: { id: string; number: string | null; status: import('./documents').DocStatus }[];
  version: number;
  actions: import('./documents').WorkflowAction[];
}

// ---------- Inward gate pass ----------

/** One inward gate pass = one purchase order (one delivery); a PO can have several, one per delivery. */
export const gatePassSchema = z.object({
  docDate: isoDate,
  supplierId: uuid('Select the supplier'),
  poId: uuid('Select the purchase order'),
  vehicleNo: opt(30),
  driverName: opt(80),
  supplierDnNo: opt(60),
  /** The supplier's delivery note date and when the vehicle came in (HH:MM). */
  supplierDnDate: z.preprocess((v) => (v === '' ? null : v), isoDate.nullable().optional()),
  gateInTime: z.preprocess((v) => (v == null ? '' : v), z.string().regex(/^(|([01]\d|2[0-3]):[0-5]\d)$/, 'Enter the time as HH:MM')).default(''),
  notes: opt(500),
  // Lines left at 0 are dropped (the form says so), not refused.
  lines: z
    .array(z.object({
      poLineId: uuid('Pick the order line'),
      qty: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number({ error: 'Enter the quantity arrived' }).min(0, 'Cannot be negative').max(1e9)),
      /** More arrived than is open on the PO: why (recorded, shown in red; the goods receipt decides). */
      overReason: opt(200),
      notes: opt(300),
    }))
    .max(300)
    .transform((ls) => ls.filter((l) => l.qty > 0))
    .refine((ls) => ls.length > 0, 'Add at least one line (lines left at 0 are dropped)'),
});
export type GatePassInput = z.infer<typeof gatePassSchema>;

// ---------- Planning engine ("this will affect…") ----------

const isoTs = z.string().datetime({ offset: true });

/** A change to the production plan. */
export const planChangeRequestSchema = z.discriminatedUnion('kind', [
  /** Drag one booking to a new time and/or another employee. */
  z.object({ kind: z.literal('move_slot'), allocationId: z.string().uuid(), startAt: isoTs, employeeId: z.string().uuid().optional() }),
  /** Shift a whole job (all its not-started bookings) by N minutes. */
  z.object({ kind: z.literal('move_job'), jobId: z.string().uuid(), deltaMinutes: z.number().int().min(-60 * 24 * 60).max(60 * 24 * 60) }),
  /** Rush order: bring a job's not-started work as early as possible; others are pushed to make room. */
  z.object({ kind: z.literal('rush_job'), jobId: z.string().uuid() }),
]);
export type PlanChangeRequest = z.infer<typeof planChangeRequestSchema>;
export const planApplySchema = z.object({ request: planChangeRequestSchema, reason: z.string().trim().max(500).default('') });

export interface PlanMove {
  allocationId: string;
  jobId: string;
  jobNumber: string | null;
  operationId: string;
  fromEmployeeId: string;
  fromEmployee: string;
  toEmployeeId: string;
  toEmployee: string;
  from: [string, string];
  to: [string, string];
  /** requested = the user's own move; employee_busy / sequence = pushed by the ripple. */
  reason: 'requested' | 'employee_busy' | 'sequence';
  frozen: boolean;
}

export interface PlanImpact {
  moved: number;
  jobs: { jobId: string; number: string | null; orderId: string; orderNumber: string | null; customer: string | null; finishBefore: string; finishAfter: string; shiftMinutes: number; committedDate: string | null; deliveryDate: string | null; daysLate: number; affectsDelivery: boolean }[];
  lateOrders: number;
  deliveryAffected: number;
  employees: number;
  overtimeMinutes: number;
  frozen: number;
  conflicts: { kind: 'calendar' | 'busy' | 'sequence' | 'overtime'; message: string; allocationId?: string }[];
  /** Free colleagues in the same department at the requested time. */
  alternatives: { employeeId: string; name: string }[];
  customersToNotify: string[];
}

export interface PlanPreview {
  request: PlanChangeRequest;
  moves: PlanMove[];
  impact: PlanImpact;
  blocked: boolean;
  needsApproval: boolean;
  needsOverride: boolean;
  committedDateRule: 'APPROVAL' | 'WARN' | 'BLOCK';
}

export const planSuggestSchema = z.object({
  earliest: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  operations: z.array(z.object({ operationTypeId: z.string().uuid(), plannedMinutes: z.coerce.number().int().min(0).max(60 * 24 * 30) })).min(1).max(40),
});
export type PlanSuggestInput = z.infer<typeof planSuggestSchema>;
export interface PlanSuggestion {
  operations: { index: number; employeeId: string | null; employee: string | null; allocations: { employeeId: string; startAt: string; endAt: string }[] }[];
  /** 'YYYY-MM-DDTHH:MM' (UAE) when the last operation ends. */
  finish: string | null;
  warnings: string[];
}

// ---------- Quality check & job board ----------

export type QcResult = 'PASS' | 'REWORK';
export const qcSchema = z
  .object({
    result: z.enum(['PASS', 'REWORK']),
    notes: z.string().trim().max(1000).default(''),
    rework: z.array(z.object({ operationTypeId: z.string().uuid(), plannedMinutes: z.coerce.number().int().min(0).max(100000).default(0), instructions: z.string().trim().max(500).default('') })).max(20).default([]),
  })
  .superRefine((v, ctx) => {
    if (v.result === 'REWORK' && !v.rework.length) ctx.addIssue({ code: 'custom', path: ['rework'], message: 'Add at least one rework operation' });
    if (v.result === 'REWORK' && v.notes.length < 3) ctx.addIssue({ code: 'custom', path: ['notes'], message: 'Say what has to be fixed' });
  });
export type QcInput = z.infer<typeof qcSchema>;

export interface JobBoardCard {
  id: string;
  number: string | null;
  stage: JobStage;
  customer: string | null;
  orderNumber: string | null;
  items: string;
  units: number;
  plannedEnd: string;
  committedDate: string | null;
  late: boolean;
  progress: number;
  currentOp: string | null;
  people: string[];
  rework: number;
  waitingHours: number | null;
}

// ---------- Desk entry of job cards (KPO) ----------

const hm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
export const jobCardSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  rows: z
    .array(z.object({ operationId: z.string().uuid(), employeeId: z.string().uuid('Choose the worker'), from: hm, to: hm, notes: z.string().trim().max(300).default('') }).refine((r) => r.to > r.from, { path: ['to'], message: 'Must be after the start' }))
    .max(300)
    .default([]),
  complete: z.array(z.string().uuid()).max(200).default([]),
});
export type JobCardInput = z.infer<typeof jobCardSchema>;

export interface JobCardDay {
  date: string;
  operations: { id: string; job: { id: string; number: string | null; customer: string | null }; seq: number; operation: string; status: OpStatus; isRework: boolean; plannedMinutes: number; booked: { id: string; name: string }[]; logged: { id: string; employeeId: string; employee: string; from: string; to: string; minutes: number }[] }[];
}
export interface JobCardResult {
  rows: { index: number; ok: boolean; error: string | null }[];
  completed: { operationId: string; ok: boolean; error: string | null; jobStage: JobStage | null; fgNumber: string | null }[];
}
export interface JobCardsMissing {
  cards: { date: string; employee: string; employeeId: string; operationId: string; operation: string; job: { id: string; number: string | null } }[];
  overdue: { operationId: string; operation: string; status: OpStatus; plannedEnd: string; daysLate: number; job: { id: string; number: string | null; customer: string | null } }[];
}
export interface JobTimelineEntry { at: string; kind: string; label: string; number: string | null; status: string | null; docId: string | null; docType: string | null }

// ---------- Planning Studio ----------

export interface CapacityCell { day: string; available: number; booked: number; off: 'holiday' | 'leave' | 'weekly_off' | null }
export interface CapacityGrid {
  days: string[];
  sections: { id: string | null; name: string; available: number[]; booked: number[] }[];
  employees: { id: string; name: string; sectionId: string | null; cells: CapacityCell[] }[];
}

export const SKILL_LEVELS = { 1: 'Learner', 2: 'Skilled', 3: 'Expert' } as const;
export const skillsSaveSchema = z.object({
  rows: z.array(z.object({ employeeId: z.string().uuid(), operationTypeId: z.string().uuid(), level: z.number().int().min(0).max(3) })).max(5000),
});
export type SkillsSaveInput = z.infer<typeof skillsSaveSchema>;
export interface SkillsMatrix {
  operations: { id: string; name: string; departmentId: string | null }[];
  employees: { id: string; name: string; departmentId: string | null; department: string }[];
  skills: { employeeId: string; operationTypeId: string; level: number }[];
}

export type PlanIssueKind = 'absent' | 'sequence' | 'skill' | 'material' | 'overload' | 'overtime';
export interface PlanIssue {
  kind: PlanIssueKind;
  severity: 'danger' | 'warning';
  message: string;
  day: string;
  jobId: string;
  jobNumber: string | null;
  employee: string | null;
  allocationId: string | null;
  /** A ready-made change that fixes it (previewed through the impact panel before applying). */
  fix: { label: string; request: PlanChangeRequest } | null;
}

export interface PlanProposal {
  id: string;
  createdAt: string;
  trigger: string;
  reason: string;
  request: PlanChangeRequest;
  impact: PlanImpact;
  moves: number;
}

export interface StandardTimeRow {
  operationTypeId: string;
  name: string;
  defaultMinutes: number;
  samples: number;
  /** Median of actual ÷ planned over recent finished operations (1 = on plan). */
  ratio: number | null;
  suggested: number | null;
  recent: number[];
}

/** Production Setup › checklist: what is missing before planning runs smoothly, each with where to fix it. */
export interface SetupCheck {
  key: 'no_section' | 'empty_category' | 'no_main' | 'skills_empty' | 'skills_missing' | 'no_rates' | 'no_responsible';
  severity: 'warning' | 'info';
  message: string;
  items: string[];
  fix: { module: string; screen: string; tab?: string; label: string };
}
